/**
 * Every string the PDF reader shows (FIL-8b).
 *
 * The frame's own controls and the states of fetching bytes are
 * `Viewer/viewer.copy.ts`; what is here is paging and zooming a document.
 *
 * **The percentages are functions returning template strings**, not literals
 * with a `%` in them — `react/jsx-no-literals` is an error in this workspace
 * and `%` is not among the allowed strings.
 *
 * Sentence case, no emoji.
 */
export const PDF_VIEWER_COPY = {
  loading: 'Opening the document',
  page: {
    label: 'Page',
    /** The denominator beside the page input. Read as "of 6". */
    of: (total: number) => `of ${String(total)}`,
    position: (current: number, total: number) => `Page ${String(current)} of ${String(total)}`,
    /**
     * The arrows either side of the page input.
     *
     * **These step pages, and there is deliberately nothing that steps files.**
     * `ImageViewer` has previous/next because a folder of photographs is a set
     * you compare across; a document is one thing you stay inside, and FIL-8b
     * fixes that difference rather than leaving it to taste. The reader had no
     * arrows at all until the toolbars were consolidated — the input alone was
     * the only way to move a page without scrolling, which is fine with a
     * keyboard and poor with a thumb.
     */
    previous: 'Previous page',
    next: 'Next page',
  },
  zoom: {
    in: 'Zoom in',
    out: 'Zoom out',
    level: (scale: number) => `${String(Math.round(scale * 100))}%`,
    fitPage: 'Fit page',
    fitWidth: 'Fit width',
    /** The control is a toggle, so its label names what pressing it will do. */
    toFitWidth: 'Fit to width',
    toFitPage: 'Fit whole page',
  },
  rotate: 'Rotate clockwise',
  errors: {
    /**
     * The two refusals pdf.js names, given the reader's own words.
     *
     * A password-protected filing is not a broken one and must not read as
     * though it were — the person holding it can act on the difference, and
     * nothing in this product is going to remove the protection for them.
     */
    password:
      'This document is password-protected, so it cannot be opened here. Download it to open it in a PDF reader.',
    invalid: 'This document is damaged and could not be read. Downloading it may still work.',
    unknown: 'This document could not be opened.',
    retry: 'Try again',
  },
} as const;
